import millify from "millify";
import { DEFAULT_FORMAT_PRICE_OPTIONS } from "./constants";
import { IAddress, IFormatPriceOptions } from "./types";
import {
  getSelectItemsFromCountry,
  isAddressValidated,
  isStateFieldNeeded,
} from "@common/utils/data";

export function formatList(values: string): string[] {
  return values.split(",").map((value) => value.trim());
}

export function tokenize(str: string): string {
  return (
    str
      .toLowerCase()
      .normalize("NFD")
      // latin characters only (no accents)
      .replace(/[\u0300-\u036f]/g, "")
      // replace special characters with spaces
      .replace(/[^\w\s]/gi, " ")
      // compact spaces
      .replace(/\s\s+/g, " ")
      .trim()
      // replace spaces with -
      .replace(/\s+/g, "-")
  );
}

export function generateId(prefix?: string): string {
  return `${prefix ? `${prefix}_` : ""}${Math.floor(
    Math.random() * Number.MAX_SAFE_INTEGER,
  )}`;
}

// email

// from http://emailregex.com
// const REGEXP_EMAIL = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// used by input[type=email]
// const REGEXP_EMAIL = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
// from https://github.com/metabase/metabase/blob/ce939219f8a3e28ecb00deb232154780a1dcfcef/frontend/@metabase/lib/utils.js#L146-L149
// const REGEXP_EMAIL =
//   /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// from https://github.com/segmentio/is-email/blob/master/lib/index.js
const REGEXP_EMAIL =
  /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export function isEmail(emailStr: string): boolean {
  return REGEXP_EMAIL.test(emailStr);
}

// password

export const PASSWORD_RULES = "At least 15 characters";

export function isPassword(password: string): boolean {
  return /^.{15,}$/.test(password);
}

export const formatBoolean = (value?: boolean) => (value ? "✓" : "✗");

// price

export function formatPrice(
  price: string | number,
  options: IFormatPriceOptions = DEFAULT_FORMAT_PRICE_OPTIONS,
): string {
  const formattedPrice = typeof price !== "number" ? Number(price) : price;

  if (isNaN(formattedPrice)) {
    throw new Error(`formatPrice, invalid price: ${price}`);
  }

  // When rounding is disabled, show the exact value at the configured precision
  // (used for sub-cent per-unit rates, see PRECISE_FORMAT_PRICE_OPTIONS).
  if (!options?.roundUp) {
    return formattedPrice.toLocaleString(options.locale, options);
  }

  // Ceil to whole cents to mirror how Stripe bills decimal/usage lines (it
  // rounds each invoice line up). Strip floating-point noise first (toFixed)
  // so values like 0.07 * 100 = 7.0000000000000001, or near-zero subtraction
  // residue, don't ceil a whole cent too high.
  // https://docs.stripe.com/products-prices/manage-prices#rounding
  const rawCents = Math.ceil(Number((formattedPrice * 100).toFixed(6)));
  const wholeCents = Object.is(rawCents, -0) ? 0 : rawCents; // normalize -0 -> 0
  if (wholeCents > 0 && wholeCents < 100) {
    return `${wholeCents}¢`;
  }

  const ceiledPrice = wholeCents / 100;
  return ceiledPrice.toLocaleString(options.locale, options);
}

// quantity

export function formatQuantity(quantity: number, precision = 3): string {
  if (quantity < 0) {
    throw new Error(`formatQuantity, invalid quantity: ${quantity}`);
  }

  return millify(quantity, {
    precision,
    space: false,
    units: ["", "k", "M", "B", "T", "P", "E"],
  });
}

// capitalize

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function capitalizeEveryWords(str: string): string {
  return str
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ");
}

export function prettyPrintValue(value: string): string {
  return capitalize(value)
    .split("-") // remove -
    .join(" ")
    .split("_") // remove _
    .join(" ")
    .split(".") // remove .
    .join(" ")
    .split(" ")
    .map((word) => {
      // ID, URL and DNS
      // are always in uppercase
      if (
        word.toLowerCase() === "id" ||
        word.toLowerCase() === "url" ||
        word.toLowerCase() === "dns" ||
        word.toLowerCase() === "dwh" ||
        word.toLowerCase() === "etl" ||
        word.toLowerCase() === "ai" ||
        word.toLowerCase() === "sql"
      ) {
        return word.toUpperCase();
      }

      // brands
      if (
        word.toLowerCase() === "python" ||
        word.toLowerCase() === "stripe" ||
        word.toLowerCase() === "metabase" ||
        word.toLowerCase() === "cloud" ||
        word.toLowerCase() === "starter"
      ) {
        return capitalize(word);
      }

      return word;
    })
    .join(" ")
    .replace("self hosted", "Self-hosted")
    .trim();
}

// plural

const plural: Record<string, string> = {
  "(quiz)$": "$1zes",
  "^(ox)$": "$1en",
  "([m|l])ouse$": "$1ice",
  "(matr|vert|ind)ix|ex$": "$1ices",
  "(x|ch|ss|sh)$": "$1es",
  "([^aeiouy]|qu)y$": "$1ies",
  "(hive)$": "$1s",
  "(?:([^f])fe|([lr])f)$": "$1$2ves",
  "(shea|lea|loa|thie)f$": "$1ves",
  sis$: "ses",
  "([ti])um$": "$1a",
  "(tomat|potat|ech|her|vet)o$": "$1oes",
  "(bu)s$": "$1ses",
  "(alias)$": "$1es",
  "(octop)us$": "$1i",
  "(ax|test)is$": "$1es",
  "(us)$": "$1es",
  "([^s]+)$": "$1s",
};

const irregular: Record<string, string> = {
  move: "moves",
  foot: "feet",
  goose: "geese",
  sex: "sexes",
  child: "children",
  man: "men",
  tooth: "teeth",
  person: "people",
};

const uncountable: string[] = [
  "sheep",
  "fish",
  "deer",
  "moose",
  "series",
  "species",
  "money",
  "rice",
  "information",
  "equipment",
  "bison",
  "cod",
  "offspring",
  "pike",
  "salmon",
  "shrimp",
  "swine",
  "trout",
  "aircraft",
  "hovercraft",
  "spacecraft",
  "sugar",
  "tuna",
  "you",
  "wood",
];

export function pluralize(word: string, amount?: number): string {
  if (amount !== undefined && amount <= 1) {
    return word;
  }

  // uncountable
  if (uncountable.indexOf(word.toLowerCase()) >= 0) {
    return word;
  }

  // irregular
  for (const w in irregular) {
    const pattern = new RegExp(`${w}$`, "i");
    const replace = irregular[w];
    if (pattern.test(word)) {
      return word.replace(pattern, replace);
    }
  }

  // using regular expressions
  for (const reg in plural) {
    const pattern = new RegExp(reg, "i");
    if (pattern.test(word)) {
      return word.replace(pattern, plural[reg]);
    }
  }

  return word;
}

export function leftPad(value: string | number): string {
  if (
    (typeof value === "string" && value.length === 1) ||
    (typeof value === "number" && value < 10)
  ) {
    return `0${value}`;
  }

  return `${value}`;
}

// Parse a string into a number at base 10.
//
// Compared to `Number.parseInt` this is safe to be used as argument to e.g. `Array.prototype.map`,
// which passes additional arguments that would be misinterpreted by `Number.parseInt`:
// * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/map
// * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/parseInt
export const parseDecimalNumber = (value: string): number =>
  parseInt(value, 10);

// JSON

export function isValidJSON(str: string): boolean {
  if (str.trim().length === 0) {
    return false;
  }

  try {
    JSON.parse(str.trim());
    return true;
  } catch (err: any) {
    return false;
  }
}

export function formatAddress(
  address: {
    street?: IAddress["street"] | null;
    street2?: IAddress["street2"] | null;
    zip?: IAddress["zip"] | null;
    city?: IAddress["city"] | null;
    region?: IAddress["region"] | null;
    country?: IAddress["country"] | null;
  },
  countries: Record<string, string>,
): string {
  const { street, street2, zip, city, region, country } = address;
  const addressContent: string[] = [];

  if (street && street.length > 0) {
    addressContent.push(street);
  }
  if (street2 && street2.length > 0) {
    addressContent.push(street2);
  }

  // zip + city
  const zipCity: string[] = [];
  if (zip && zip.length > 0) {
    zipCity.push(zip);
  }
  if (city && city.length > 0) {
    zipCity.push(city);
  }
  if (zipCity.length > 0) {
    addressContent.push(zipCity.join(" "));
  }

  if (region && region.length > 0) {
    if (country && isStateFieldNeeded(country)) {
      if (isAddressValidated(country)) {
        const state = getSelectItemsFromCountry(country).find(
          ({ value, label }) => region === value || region === label,
        );
        addressContent.push(state?.name || region);
      }
      // Other
      else {
        addressContent.push(region);
      }
    }
  }

  if (country && country.length > 0) {
    const countryKey = Object.keys(countries).find(
      (key) => key === country || countries[key] === country,
    );
    const countryName = countryKey ? countries[countryKey] : country;
    addressContent.push(countryName);
  }

  return addressContent.join(", ");
}
